import { generatePath, Link } from 'react-router'
import { Navigation } from 'swiper/modules'
import { Swiper, SwiperSlide } from 'swiper/react'

import { useMenu } from '@entities/Menu'

import type { LandingBlockProps } from '@features/landing-page-builder/model/blockRegistry'

import { URL_PAGE_CATALOG_1ST_LVL } from '@shared/contants'
import { CATEGORY_DEFAULT_ICON_PATH } from '@shared/contants/product'
import { cn } from '@shared/lib/cn'

import { useCategorySlugs } from './parts/useCategorySlugs'

import styles from './LandingMenuBlock.module.scss'

/*
  menu: a CMS-managed menu rendered as a row of links — the same block type the
  main page config uses (an event landing extends the main page contract).

  Unlike the main page's MenuBlock, this renders the menu the payload actually
  names, so a landing can carry its own shortcuts instead of the catalog line.

  Item destinations depend on link_type:
    category -> link_url holds a category ID, resolved to a catalog slug
    other    -> link_url is a URL; absolute ones open as plain anchors so the
                router doesn't try to match another host

  payload.view picks the icon shape ("round" | "square"); round is the default
  so menus authored before the admin gained the selector keep their look.

  Items pointing at a category that is unavailable for the current shop are
  dropped (a /catalog/<id> link would 404), and the whole section disappears when
  nothing is left — no orphan heading.
*/
interface MenuPayload {
  code?: string;
  name?: string;
  title?: string;
  /** Icon shape chosen in the admin; round when unset (older menus). */
  view?: 'round' | 'square' | string;
}

export default function LandingMenuBlock ({ payload, shopId }: LandingBlockProps) {
  const { code, name, title, view } = (payload ?? {}) as MenuPayload

  const isSquare = view === 'square'

  const { menu, loading, error } = useMenu({ code: code ?? '' })

  const items = menu?.menuItems ?? []

  const categoryIds = items
    .filter((item) => item.link_type === 'category' && item.link_url)
    .map((item) => String(item.link_url))

  const { categoriesById, loading: loadingCategories } = useCategorySlugs(categoryIds, shopId)

  const links = items
    .map((item) => {
      if (item.link_type === 'category') {
        const category = categoriesById.get(String(item.link_url))

        /*
          The menu item already carries everything needed to display it (title,
          image) — the category lookup only supplies the slug the catalog route
          needs. When it does not resolve for the current shop the item is still
          shown, just not clickable, rather than vanishing from a curated menu.
        */
        return {
          key: item.id,
          label: item.title || category?.name || '',
          color: item.text_color,
          icon: item.icon_url || category?.iconUrl || '',
          href: category?.slug
            ? generatePath(URL_PAGE_CATALOG_1ST_LVL, { categorySlug: category.slug })
            : undefined,
          external: false,
        }
      }

      if (!item.link_url) {
        return null
      }

      return {
        key: item.id,
        label: item.title,
        color: item.text_color,
        icon: item.icon_url || '',
        href: item.link_url,
        external: /^https?:\/\//.test(item.link_url),
      }
    })
    .filter((link): link is NonNullable<typeof link> => Boolean(link))

  const heading = name || title

  if (!code || error || loading || loadingCategories || links.length === 0) {
    return null
  }

  return (
    <section className={styles.root}>
      {heading && <h2 className={styles.root__title}>{heading}</h2>}

      <Swiper
        className={styles.root__slider}
        slidesPerView="auto"
        spaceBetween={12}
        modules={[ Navigation ]}
        navigation
      >
        {links.map((link) => {
          const body = (
            <>
              <span
                className={cn(styles.item__media, {
                  [styles['item__media--square']]: isSquare,
                })}
              >
                <img
                  className={cn(styles.item__icon, {
                    [styles['item__icon--plug']]: !link.icon,
                  })}
                  src={link.icon || CATEGORY_DEFAULT_ICON_PATH}
                  alt=""
                  loading="lazy"
                />
              </span>
              <span
                className={styles.item__label}
                style={link.color ? { color: link.color } : undefined}
              >
                {link.label}
              </span>
            </>
          )

          return (
            <SwiperSlide className={styles.root__slide} key={link.key}>
              {!link.href ? (
                <span className={styles.item}>{body}</span>
              ) : link.external ? (
                <a
                  className={styles.item}
                  href={link.href}
                  target="_blank"
                  rel="noreferrer noopener"
                >
                  {body}
                </a>
              ) : (
                <Link className={styles.item} to={link.href}>{body}</Link>
              )}
            </SwiperSlide>
          )
        })}
      </Swiper>
    </section>
  )
}
